/**
 * ============================================================================
 * VenueInfoNew - Venue Information Management
 * ============================================================================
 *
 * Complete venue management interface with TanStack Query integration
 * - Venue details & images with carousel
 * - Operating hours management
 * - Sports & pricing management
 * - Blackout dates & temporary closures
 * - Cancellation & refund policies
 * - Dynamic pricing rules
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from '../utils/currency';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useVenueRole } from '../hooks/useVenueRole';
import { useConfirmation } from '../contexts/ConfirmationContext';
import Toast from '../components/Toast';
import {
  AlertCircle,
  RefreshCw,
  MapPin,
  Camera,
  ChevronLeft,
  ChevronRight,
  X,
  Clock,
  Edit,
  Save,
  DollarSign,
  Tag,
  Calendar,
  Shield,
  CheckCircle,
  Trash2,
  TrendingUp,
  Plus,
  Power,
  Info
} from 'lucide-react';

// Hooks
import { useVenueData, useUpdateOperatingHours } from '../hooks/useVenueData';
import { useCreateBlackoutDate, useDeleteBlackoutDate, type BlackoutDate } from '../hooks/useBlackoutDates';
import {
  usePolicyData,
  useCreatePolicyFromTemplate,
  useCreateCustomPolicy,
  useActivatePolicy,
  useDeletePolicy,
  type BookingType,
  type VenuePolicy
} from '../hooks/usePolicies';
import { PolicySummaryCard } from '../components/PolicySummaryCard';
import {
  usePricingRules,
  useCreatePricingRule,
  useTogglePricingRule,
  useDeletePricingRule,
  type RuleType,
  type AdjustmentType,
  type CreatePricingRuleData
} from '../hooks/useDynamicPricing';
import vendorApi from '../services/api';
import type { OperatingHours } from '../types';

/**
 * Venue Information page - Complete venue management interface
 */
export default function VenueInfoNew() {
  const { user } = useAuth();
  const { confirm } = useConfirmation();
  const { can } = useVenueRole();
  const userVenue = user?.venues?.[0];

  // Capability gating (staff-access): the Venue Info page bundles four
  // capability domains. An owner (or admin) passes all of these; a staff
  // member sees only what their capabilities cover — the rest render
  // read-only / greyed, and their data queries don't fire (no 403 storm).
  const canManagePricing = can('pricing.manage'); // Dynamic Pricing rules
  const canManageSlots = can('slots.manage');     // Temporary closures

  // ============================================================================
  // STATE - Toast Notifications
  // ============================================================================

  const [toast, setToast] = useState<{message: string; type: 'success' | 'error' | 'info'} | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type });
  };

  // ============================================================================
  // STATE - Image carousel & interactions (from original)
  // ============================================================================

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // ============================================================================
  // STATE - Operating Hours editing
  // ============================================================================

  const [isEditingHours, setIsEditingHours] = useState(false);
  const [operatingHours, setOperatingHours] = useState<OperatingHours[]>([]);

  // ============================================================================
  // STATE - Sports & Pricing editing
  // ============================================================================

  const [isEditingPricing, setIsEditingPricing] = useState(false);
  const [commissionRate, setCommissionRate] = useState<string>('15.00');
  const [sportRates, setSportRates] = useState<Record<number, {
    rate?: string;
    pricing_type?: 'hourly' | 'per_person';
  }>>({});
  const [isSavingPricing, setIsSavingPricing] = useState(false);

  // ============================================================================
  // STATE - Session Duration Settings
  // ============================================================================

  const [isEditingDuration, setIsEditingDuration] = useState(false);
  const [minSessionHours, setMinSessionHours] = useState<number>(1);
  const [maxSessionHours, setMaxSessionHours] = useState<number | null>(null);
  const [isSavingDuration, setIsSavingDuration] = useState(false);

  // ============================================================================
  // STATE - Blackout Dates
  // ============================================================================

  const [blackoutDates, setBlackoutDates] = useState<BlackoutDate[]>([]);
  const [showAddBlackout, setShowAddBlackout] = useState(false);
  const [newBlackoutDate, setNewBlackoutDate] = useState('');
  const [newBlackoutEndDate, setNewBlackoutEndDate] = useState('');
  const [newBlackoutReason, setNewBlackoutReason] = useState('');
  const [newBlackoutSportId, setNewBlackoutSportId] = useState<number | null>(null);
  const [isDateRange, setIsDateRange] = useState(false);

  // ============================================================================
  // STATE - Dynamic Pricing Rules
  // ============================================================================

  const [showAddPricingRule, setShowAddPricingRule] = useState(false);
  const [newRuleName, setNewRuleName] = useState('');
  const [newRuleDescription, setNewRuleDescription] = useState('');
  const [newRuleType, setNewRuleType] = useState<RuleType>('time_based');
  const [newAdjustmentType, setNewAdjustmentType] = useState<AdjustmentType>('fixed_amount');
  const [newAdjustmentValue, setNewAdjustmentValue] = useState('');
  const [newAppliesFromTime, setNewAppliesFromTime] = useState('');
  const [newAppliesToTime, setNewAppliesToTime] = useState('');
  const [newAppliesOnDays, setNewAppliesOnDays] = useState<number[]>([]);
  const [newAppliesFromDate, setNewAppliesFromDate] = useState('');
  const [newAppliesToDate, setNewAppliesToDate] = useState('');
  const [newPriority, setNewPriority] = useState('10');
  const [newRuleCourtId, setNewRuleCourtId] = useState<number | null>(null);
  const [newRuleSportId, setNewRuleSportId] = useState<number | null>(null);

  // ============================================================================
  // STATE - Policies
  // ============================================================================

  const [showPolicySection, setShowPolicySection] = useState(false);
  const [policyBookingType, setPolicyBookingType] = useState<BookingType>('one_time');
  const [showCustomPolicyForm, setShowCustomPolicyForm] = useState(false);
  const [showPolicyForm, setShowPolicyForm] = useState(false);

  // ONE-TIME BOOKING POLICIES
  const [acceptCancellations, setAcceptCancellations] = useState(true);
  const [cancellationTiers, setCancellationTiers] = useState<2 | 3 | 4>(4);
  const [cancellationTierValues, setCancellationTierValues] = useState([
    { hours: '48', percentage: 100 },
    { hours: '24', percentage: 75 },
    { hours: '2', percentage: 50 },
    { hours: '0', percentage: 0 },
  ]);

  const [acceptReschedules, setAcceptReschedules] = useState(true);
  const [rescheduleThreshold, setRescheduleThreshold] = useState('24');
  const [maxReschedules, setMaxReschedules] = useState('3');

  // RECURRING BOOKING - SERIES CANCELLATION
  const [acceptSeriesCancellation, setAcceptSeriesCancellation] = useState(true);
  const [seriesCancellationTiers, setSeriesCancellationTiers] = useState<2 | 3 | 4>(4);
  const [seriesCancellationTierValues, setSeriesCancellationTierValues] = useState([
    { hours: '48', percentage: 100 },
    { hours: '24', percentage: 75 },
    { hours: '2', percentage: 50 },
    { hours: '0', percentage: 0 },
  ]);

  // RECURRING BOOKING - INDIVIDUAL SESSION CANCELLATION
  const [acceptSessionCancellation, setAcceptSessionCancellation] = useState(true);
  const [sessionCancellationTiers, setSessionCancellationTiers] = useState<2 | 3 | 4>(4);
  const [sessionCancellationTierValues, setSessionCancellationTierValues] = useState([
    { hours: '48', percentage: 100 },
    { hours: '24', percentage: 75 },
    { hours: '2', percentage: 50 },
    { hours: '0', percentage: 0 },
  ]);
  const [maxSessionsCancellable, setMaxSessionsCancellable] = useState('3');

  // RECURRING BOOKING - REMAINING SESSIONS CANCELLATION
  const [acceptRemainingCancellation, setAcceptRemainingCancellation] = useState(true);
  const [remainingCancellationTiers, setRemainingCancellationTiers] = useState<2 | 3 | 4>(4);
  const [remainingCancellationTierValues, setRemainingCancellationTierValues] = useState([
    { hours: '48', percentage: 100 },
    { hours: '24', percentage: 75 },
    { hours: '2', percentage: 50 },
    { hours: '0', percentage: 0 },
  ]);

  // RECURRING BOOKING - INDIVIDUAL SESSION RESCHEDULE
  const [acceptSessionReschedule, setAcceptSessionReschedule] = useState(true);
  const [sessionRescheduleThreshold, setSessionRescheduleThreshold] = useState('24');
  const [maxReschedulesPerSession, setMaxReschedulesPerSession] = useState('2');
  const [maxTotalReschedules, setMaxTotalReschedules] = useState('10');

  // ============================================================================
  // DATA FETCHING (TanStack Query)
  // ============================================================================

  const {
    data: venueData,
    isPending,
    error,
    refetch,
  } = useVenueData(user?.id, userVenue?.id);

  const venue = venueData?.venue || null;
  const venueCourts = venueData?.courts || [];

  // Policies are editable only when the venue's policy editing is enabled AND
  // the user has policies.manage (owner-only — never grantable to staff). When
  // false, the policy section falls back to its existing read-only view.
  const canEditPolicies = venue?.policy_editing_enabled !== false && can('policies.manage');

  // Mutation for updating operating hours
  const updateOperatingHoursMutation = useUpdateOperatingHours();

  // Mutations for blackout dates
  const createBlackoutMutation = useCreateBlackoutDate();
  const deleteBlackoutMutation = useDeleteBlackoutDate();

  // Dynamic Pricing Rules with TanStack Query — only fetch when the user can
  // manage pricing (pricing.manage); otherwise the /v1/pricing-rules endpoint
  // 403s for staff and the section stays read-only.
  const { data: pricingRules = [], isLoading: isLoadingPricingRules } = usePricingRules(canManagePricing ? venue?.id : undefined);
  const createPricingRuleMutation = useCreatePricingRule();
  const togglePricingRuleMutation = useTogglePricingRule();
  const deletePricingRuleMutation = useDeletePricingRule();

  // Policies data fetching (only when section is shown)
  const {
    data: policyData,
    isPending: isPendingPolicies,
  } = usePolicyData(
    showPolicySection ? venue?.id : undefined,
    policyBookingType
  );

  const policyTemplates = policyData?.templates || [];
  const venuePolicies = policyData?.venuePolicies || [];
  const activePolicy = policyData?.activePolicy || null;
  const loadingPolicies = isPendingPolicies;

  // Policy mutations
  const createFromTemplateMutation = useCreatePolicyFromTemplate();
  const createCustomPolicyMutation = useCreateCustomPolicy();
  const activatePolicyMutation = useActivatePolicy();
  const deletePolicyMutation = useDeletePolicy();

  // ============================================================================
  // EFFECTS - Keyboard navigation for image modal (from original)
  // ============================================================================

  useEffect(() => {
    if (!isImageModalOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const validImages = venue?.images?.filter((img: any) => typeof img === 'string' && img.trim() !== '') || [];

      if (e.key === 'Escape') {
        setIsImageModalOpen(false);
      } else if (e.key === 'ArrowLeft') {
        setCurrentImageIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
      } else if (e.key === 'ArrowRight') {
        setCurrentImageIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isImageModalOpen, venue?.images]);

  // ============================================================================
  // HANDLERS - Touch/swipe for mobile (from original)
  // ============================================================================

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    const validImages = venue?.images?.filter((img: any) => typeof img === 'string' && img.trim() !== '') || [];

    if (distance > minSwipeDistance) {
      // Swiped left - next image
      setCurrentImageIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
    } else if (distance < -minSwipeDistance) {
      // Swiped right - previous image
      setCurrentImageIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
    }
  };

  // ============================================================================
  // EFFECTS - Initialize operating hours from venue data
  // ============================================================================

  // Helper function to transform venue operating hours to local state format
  // Always returns all 7 days, defaulting missing days to "Closed"
  const ALL_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  const getOriginalOperatingHours = (): OperatingHours[] => {
    const hoursMap: Record<string, OperatingHours> = {};

    // Initialize all 7 days as closed by default
    ALL_DAYS.forEach(day => {
      hoursMap[day] = { day, isOpen: false, openTime: '', closeTime: '' };
    });

    // Override with actual data from API
    if (venue?.operating_hours && Array.isArray(venue.operating_hours)) {
      venue.operating_hours.forEach((hour: any) => {
        const dayKey = hour.day_of_week?.toLowerCase();
        if (dayKey && hoursMap[dayKey]) {
          hoursMap[dayKey] = {
            day: hour.day_of_week,
            isOpen: !hour.is_closed,
            openTime: hour.opening_time || '',
            closeTime: hour.closing_time || '',
          };
        }
      });
    }

    return ALL_DAYS.map(day => hoursMap[day]);
  };

  // Reset operating hours to original values (used for cancel)
  const resetOperatingHours = () => {
    setOperatingHours(getOriginalOperatingHours());
  };

  useEffect(() => {
    resetOperatingHours();
  }, [venue?.operating_hours]);

  // ============================================================================
  // EFFECTS - Initialize sports pricing from venue data
  // ============================================================================

  // Helper function to get original sport rates from venue data
  const getOriginalSportRates = (): Record<number, {rate?: string; pricing_type?: 'hourly' | 'per_person'}> => {
    if (!venue?.sports || !Array.isArray(venue.sports)) return {};

    const rates: Record<number, {rate?: string; pricing_type?: 'hourly' | 'per_person'}> = {};
    venue.sports.forEach((sport: any) => {
      if (sport.pivot) {
        // Check for custom hourly rate
        if (sport.pivot.custom_hourly_rate) {
          rates[sport.id] = {
            rate: sport.pivot.custom_hourly_rate,
            pricing_type: 'hourly'
          };
        }
        // Check for custom per person rate
        else if (sport.pivot.custom_per_person_rate) {
          rates[sport.id] = {
            rate: sport.pivot.custom_per_person_rate,
            pricing_type: 'per_person'
          };
        }
      }
    });
    return rates;
  };

  // Reset sport rates to original values (used for cancel)
  const resetSportRates = () => {
    setSportRates(getOriginalSportRates());
  };

  useEffect(() => {
    if (venue?.commission_rate) {
      setCommissionRate(venue.commission_rate.toString());
    }

    if (venue?.sports && Array.isArray(venue.sports)) {
      setSportRates(getOriginalSportRates());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue?.commission_rate, venue?.sports]);

  // ============================================================================
  // EFFECTS - Initialize capacity settings from venue data
  // ============================================================================

  useEffect(() => {
    if (venue) {
      setMinSessionHours(venue.min_session_hours || 1);
      setMaxSessionHours(venue.max_session_hours || null);
    }
  }, [venue?.min_session_hours, venue?.max_session_hours]);

  // ============================================================================
  // EFFECTS - Initialize blackout dates from venue data
  // ============================================================================

  useEffect(() => {
    if (venueData?.blackoutDates && Array.isArray(venueData.blackoutDates)) {
      setBlackoutDates(venueData.blackoutDates);
    }
  }, [venueData?.blackoutDates]);

  // ============================================================================
  // HANDLERS - Operating Hours save
  // ============================================================================

  const saveOperatingHours = async () => {
    if (!venue?.id) return;

    // ✅ Validate open days have both times set
    // Note: Overnight hours are allowed (e.g., 15:00 - 00:00)
    for (const hour of operatingHours) {
      if (hour.isOpen) {
        if (!hour.openTime || !hour.closeTime) {
          showToast('Please set both opening and closing times for all open days.', 'error');
          return;
        }
      }
    }

    try {
      await updateOperatingHoursMutation.mutateAsync({
        venueId: venue.id,
        hours: operatingHours,
      });

      setIsEditingHours(false);
      showToast('Operating hours updated successfully!', 'success');
    } catch (err: any) {
      console.error('Operating hours save error:', err);
      showToast('Failed to save operating hours. Please try again.', 'error');
    }
  };

  // ============================================================================
  // HANDLERS - Sports Pricing save
  // ============================================================================

  const savePricing = async () => {
    if (!venue?.id) return;

    setIsSavingPricing(true);
    try {
      // Prepare sport-specific pricing data only
      const sportRatesArray = Object.entries(sportRates)
        .filter(([sportId, rates]) => rates.rate && rates.rate.trim() !== '')
        .map(([sportId, rates]) => {
          const rateData: any = {
            sport_id: parseInt(sportId),
          };

          // Add rate to the appropriate field based on pricing type
          if (rates.pricing_type === 'per_person') {
            rateData.per_person_rate = parseFloat(rates.rate!);
          } else {
            // Default to hourly
            rateData.hourly_rate = parseFloat(rates.rate!);
          }

          return rateData;
        });

      const pricingData: any = {};
      if (sportRatesArray.length > 0) {
        pricingData.sport_rates = sportRatesArray;
      }

      await vendorApi.updateVenuePricing(venue.id, pricingData);
      await refetch(); // Refetch venue data to get updated pricing

      setIsEditingPricing(false);
      showToast('Pricing updated successfully! Sport-specific rates have been saved.', 'success');
    } catch (err: any) {
      console.error('Pricing save error:', err);
      showToast('Failed to save pricing. Please try again.', 'error');
    } finally {
      setIsSavingPricing(false);
    }
  };

  // ============================================================================
  // HANDLERS - Session Duration Settings save
  // ============================================================================

  const saveDurationSettings = async () => {
    if (!venue?.id) return;

    // Validation
    if (minSessionHours < 0.5) {
      showToast('Minimum session duration must be at least 0.5 hours.', 'error');
      return;
    }

    if (maxSessionHours !== null && maxSessionHours < minSessionHours) {
      showToast('Maximum session duration must be greater than or equal to minimum session duration.', 'error');
      return;
    }

    setIsSavingDuration(true);
    try {
      await vendorApi.updateVenueCapacity(venue.id, {
        min_session_hours: minSessionHours,
        max_session_hours: maxSessionHours,
      });

      await refetch(); // Refetch venue data to get updated settings

      setIsEditingDuration(false);
      showToast('Session duration settings updated successfully!', 'success');
    } catch (err: any) {
      console.error('Duration save error:', err);
      showToast(err.message || 'Failed to save session duration settings. Please try again.', 'error');
    } finally {
      setIsSavingDuration(false);
    }
  };

  // ============================================================================
  // HANDLERS - Blackout Dates
  // ============================================================================

  const addBlackoutDate = async () => {
    if (!canManageSlots) {
      showToast("You don't have permission to manage temporary closures.", 'error');
      return;
    }
    if (!newBlackoutDate || !newBlackoutReason.trim()) {
      showToast('Please fill in both date and reason for closure.', 'error');
      return;
    }

    if (isDateRange && (!newBlackoutEndDate || newBlackoutEndDate <= newBlackoutDate)) {
      showToast('Please select a valid end date for the range.', 'error');
      return;
    }

    try {
      if (venue?.id) {
        // Check for booking conflicts
        const checkStartDate = newBlackoutDate;
        const checkEndDate = isDateRange && newBlackoutEndDate ? newBlackoutEndDate : newBlackoutDate;

        console.log('🔍 Checking for booking conflicts:', { checkStartDate, checkEndDate, sportId: newBlackoutSportId });

        // Fetch existing bookings for the date range
        const existingBookings = await vendorApi.getBookings(venue.id, {
          start_date: checkStartDate,
          end_date: checkEndDate,
          status: 'confirmed'
        });

        console.log('📅 Existing bookings found:', existingBookings);

        // Filter bookings by sport if a specific sport is selected
        const conflictingBookings = newBlackoutSportId
          ? existingBookings.filter((booking: any) => booking.sport_id === newBlackoutSportId)
          : existingBookings;

        if (conflictingBookings.length > 0) {
          // Show detailed conflict warning
          const bookingDetails = conflictingBookings.map((b: any) => {
            const bookingDate = new Date(b.booking_date).toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            });

            const formatTime = (time: string) => {
              if (!time) return '';
              return time.substring(0, 5);
            };

            const timeSlot = `${formatTime(b.start_time)} - ${formatTime(b.end_time)}`;
            const sport = b.court?.sport?.name || b.sport?.name || 'Unknown Sport';
            const customer = b.user?.name || b.customer?.name || 'Unknown Customer';
            const reference = b.booking_reference || b.reference || `#${b.id}`;

            return `• ${bookingDate} at ${timeSlot} - ${sport}\n  Customer: ${customer} | Ref: ${reference}`;
          }).join('\n\n');

          const sportText = newBlackoutSportId
            ? `for ${venue.sports.find((s: any) => s.id === newBlackoutSportId)?.name}`
            : 'for all sports';

          const errorMessage = `❌ CANNOT ADD TEMPORARY CLOSURE\n\n` +
            `There ${conflictingBookings.length === 1 ? 'is' : 'are'} ${conflictingBookings.length} existing booking${conflictingBookings.length > 1 ? 's' : ''} ${sportText} on the selected date${isDateRange ? 's' : ''}:\n\n` +
            `${bookingDetails}\n\n` +
            `⚠️ You must resolve these bookings first:\n` +
            `1. Contact the customer${conflictingBookings.length > 1 ? 's' : ''} to reschedule or cancel\n` +
            `2. Process any necessary refunds\n` +
            `3. Wait for the booking${conflictingBookings.length > 1 ? 's' : ''} to be cancelled\n` +
            `4. Then try adding the temporary closure again\n\n` +
            `Temporary closure has NOT been added.`;

          showToast(errorMessage, 'error');
          console.log('❌ Blocked blackout date due to booking conflicts:', conflictingBookings);
          return;
        } else {
          console.log('✅ No booking conflicts found, proceeding with blackout date');
        }

        // Determine applies_to text
        const appliesTo = newBlackoutSportId
          ? venue.sports.find((s: any) => s.id === newBlackoutSportId)?.name || 'Unknown Sport'
          : 'All Sports';

        if (isDateRange && newBlackoutEndDate) {
          // Create multiple blackout dates for the range
          const startDate = new Date(newBlackoutDate);
          const endDate = new Date(newBlackoutEndDate);
          const newBlackouts = [];

          for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
            const dateString = date.toISOString().split('T')[0];
            await createBlackoutMutation.mutateAsync({
              venueId: venue.id,
              date: dateString,
              reason: newBlackoutReason,
              sportId: newBlackoutSportId || undefined,
            });
            newBlackouts.push({
              date: dateString,
              reason: newBlackoutReason,
              sport_id: newBlackoutSportId,
              applies_to: appliesTo
            });
          }

          setBlackoutDates([...blackoutDates, ...newBlackouts]);
          showToast(`Date range closure added successfully! (${newBlackouts.length} days)`, 'success');
        } else {
          // Single date
          await createBlackoutMutation.mutateAsync({
            venueId: venue.id,
            date: newBlackoutDate,
            reason: newBlackoutReason,
            sportId: newBlackoutSportId || undefined,
          });

          setBlackoutDates([...blackoutDates, {
            date: newBlackoutDate,
            reason: newBlackoutReason,
            sport_id: newBlackoutSportId,
            applies_to: appliesTo
          }]);
          showToast('Temporary closure added successfully!', 'success');
        }

        // Reset form
        setNewBlackoutDate('');
        setNewBlackoutEndDate('');
        setNewBlackoutReason('');
        setNewBlackoutSportId(null);
        setIsDateRange(false);
        setShowAddBlackout(false);
      }
    } catch (err: any) {
      console.error('Error adding temporary closure:', err);
      showToast('Failed to add temporary closure. Please try again.', 'error');
    }
  };

  const removeBlackoutDate = async (index: number) => {
    const confirmed = await confirm({
      title: 'Remove Temporary Closure',
      message: 'Are you sure you want to remove this temporary closure?',
      confirmLabel: 'Remove',
      cancelLabel: 'Cancel',
      variant: 'warning',
    });

    if (confirmed) {
      try {
        const blackout = blackoutDates[index];
        if (venue?.id && blackout.id) {
          await deleteBlackoutMutation.mutateAsync({
            venueId: venue.id,
            blackoutId: blackout.id,
          });
        }

        const updated = blackoutDates.filter((_, i) => i !== index);
        setBlackoutDates(updated);
        showToast('Temporary closure removed successfully!', 'success');
      } catch (err: any) {
        console.error('Error removing blackout date:', err);
        showToast('Failed to remove temporary closure. Please try again.', 'error');
      }
    }
  };

  // ============================================================================
  // HANDLERS - Dynamic Pricing Rules
  // ============================================================================

  const addPricingRule = async () => {
    if (!newRuleName.trim() || !newAdjustmentValue) {
      showToast('Please fill in rule name and adjustment value.', 'error');
      return;
    }

    // Validation based on rule type
    if (newRuleType === 'time_based' && !newAppliesFromTime) {
      showToast('Please select a start time for time-based rules.', 'error');
      return;
    }

    if (newRuleType === 'day_based' && newAppliesOnDays.length === 0) {
      showToast('Please select at least one day for day-based rules.', 'error');
      return;
    }

    if (newRuleType === 'date_range' && (!newAppliesFromDate || !newAppliesToDate)) {
      showToast('Please select both start and end dates for date-range rules.', 'error');
      return;
    }

    if (!venue?.id) {
      showToast('Venue ID not found.', 'error');
      return;
    }

    const ruleData: CreatePricingRuleData = {
      venue_id: venue.id,
      court_id: newRuleCourtId,
      sport_id: newRuleSportId,
      name: newRuleName,
      description: newRuleDescription,
      rule_type: newRuleType,
      adjustment_type: newAdjustmentType,
      adjustment_value: parseFloat(newAdjustmentValue),
      applies_from_time: newRuleType === 'time_based' ? newAppliesFromTime : null,
      applies_to_time: newRuleType === 'time_based' ? newAppliesToTime || null : null,
      applies_on_days: newRuleType === 'day_based' ? newAppliesOnDays : null,
      applies_from_date: newRuleType === 'date_range' ? newAppliesFromDate : null,
      applies_to_date: newRuleType === 'date_range' ? newAppliesToDate : null,
      priority: parseInt(newPriority),
      is_active: true,
    };

    try {
      await createPricingRuleMutation.mutateAsync(ruleData);
      showToast('Pricing rule added successfully!', 'success');

      // Reset form
      setShowAddPricingRule(false);
      setNewRuleName('');
      setNewRuleDescription('');
      setNewRuleType('time_based');
      setNewAdjustmentType('fixed_amount');
      setNewAdjustmentValue('');
      setNewAppliesFromTime('');
      setNewAppliesToTime('');
      setNewAppliesOnDays([]);
      setNewAppliesFromDate('');
      setNewAppliesToDate('');
      setNewPriority('10');
      setNewRuleCourtId(null);
      setNewRuleSportId(null);
    } catch (err: any) {
      console.error('Error adding pricing rule:', err);
      showToast(err.message || 'Failed to add pricing rule. Please try again.', 'error');
    }
  };

  const togglePricingRule = async (ruleId: number, currentStatus: boolean) => {
    try {
      await togglePricingRuleMutation.mutateAsync({ ruleId, currentStatus });
    } catch (err: any) {
      console.error('Error toggling pricing rule:', err);
      showToast('Failed to toggle pricing rule. Please try again.', 'error');
    }
  };

  const deletePricingRule = async (ruleId: number) => {
    const confirmed = await confirm({
      title: 'Delete Pricing Rule',
      message: 'Are you sure you want to delete this pricing rule?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });

    if (confirmed) {
      try {
        await deletePricingRuleMutation.mutateAsync(ruleId);
        showToast('Pricing rule deleted successfully!', 'success');
      } catch (err: any) {
        console.error('Error deleting pricing rule:', err);
        showToast('Failed to delete pricing rule. Please try again.', 'error');
      }
    }
  };

  const resetPricingRuleForm = () => {
    setShowAddPricingRule(false);
    setNewRuleName('');
    setNewRuleDescription('');
    setNewRuleType('time_based');
    setNewAdjustmentType('fixed_amount');
    setNewAdjustmentValue('');
    setNewAppliesFromTime('');
    setNewAppliesToTime('');
    setNewAppliesOnDays([]);
    setNewAppliesFromDate('');
    setNewAppliesToDate('');
    setNewPriority('10');
    setNewRuleCourtId(null);
    setNewRuleSportId(null);
  };

  // ============================================================================
  // HANDLERS - Cancellation Policies
  // ============================================================================

  const createCustomPolicy = async () => {
    if (!canEditPolicies) {
      showToast('Only the venue owner can manage policies.', 'error');
      return;
    }
    if (!venue?.id) {
      showToast('Venue ID not found', 'error');
      return;
    }

    // Build config object from form state
    const config = {
      name: `${policyBookingType === 'one_time' ? 'One-Time Booking' : policyBookingType === 'recurring' ? 'Recurring Booking' : 'Membership'} Policy`,
      booking_type: policyBookingType,
      // Explicitly send accept_cancellations flag (required by backend)
      accept_cancellations: acceptCancellations,
      // Only send tier values if cancellations are accepted
      // Send 0 instead of null for unused tiers to avoid database constraint errors
      ...(acceptCancellations && {
        tier_1_hours: parseInt(cancellationTierValues[0]?.hours || '48'),
        tier_1_percentage: cancellationTierValues[0]?.percentage ?? 100,
        tier_2_hours: cancellationTiers >= 2 ? parseInt(cancellationTierValues[1]?.hours || '24') : 0,
        tier_2_percentage: cancellationTiers >= 2 ? (cancellationTierValues[1]?.percentage ?? 75) : 0,
        tier_3_hours: cancellationTiers >= 3 ? parseInt(cancellationTierValues[2]?.hours || '2') : 0,
        tier_3_percentage: cancellationTiers >= 3 ? (cancellationTierValues[2]?.percentage ?? 50) : 0,
      }),
      // When no cancellations, set all tiers to 0 so it's clear
      ...(!acceptCancellations && {
        tier_1_hours: 0,
        tier_1_percentage: 0,
        tier_2_hours: 0,
        tier_2_percentage: 0,
        tier_3_hours: 0,
        tier_3_percentage: 0,
      }),
      allow_reschedule: acceptReschedules,
      max_reschedule_count: acceptReschedules ? parseInt(maxReschedules) : 0,
      ...(policyBookingType === 'one_time' && acceptReschedules && {
        reschedule_threshold: parseInt(rescheduleThreshold),
      }),
      ...(policyBookingType === 'recurring' && {
        // SECTION 1: Refund Policy (full series cancellation only)
        allow_series_cancellation: acceptSeriesCancellation,
        ...(acceptSeriesCancellation && {
          // Series cancellation tiers (hours before first session)
          series_tier_1_hours: parseInt(seriesCancellationTierValues[0]?.hours || '0'),
          series_tier_2_hours: seriesCancellationTiers >= 2 ? parseInt(seriesCancellationTierValues[1]?.hours || '0') : 0,
          series_tier_3_hours: seriesCancellationTiers >= 3 ? parseInt(seriesCancellationTierValues[2]?.hours || '0') : 0,
        }),
        /*
        =======================================================================
        COMMENTED OUT: Individual session cancellation policy fields
        Reason: Individual session cancellations are non-refundable.
        They only free the slot. No per-session refund tiers needed.
        =======================================================================
        allow_session_cancellation: acceptSeriesCancellation,
        session_tier_1_hours: parseInt(seriesCancellationTierValues[0]?.hours || '0'),
        session_tier_2_hours: seriesCancellationTiers >= 2 ? parseInt(seriesCancellationTierValues[1]?.hours || '0') : 0,
        session_tier_3_hours: seriesCancellationTiers >= 3 ? parseInt(seriesCancellationTierValues[2]?.hours || '0') : 0,
        */
        // SECTION 2: Session Reschedule
        allow_session_reschedule: acceptSessionReschedule,
        ...(acceptSessionReschedule && {
          session_reschedule_threshold: parseInt(sessionRescheduleThreshold),
          max_reschedules_per_session: parseInt(maxReschedulesPerSession),
        }),
      }),
      activate: true,
    };

    try {
      await createCustomPolicyMutation.mutateAsync({
        venueId: venue.id,
        bookingType: policyBookingType,
        config,
      });
      showToast('Policy saved successfully!', 'success');

      // Reset form
      setAcceptCancellations(true);
      setCancellationTiers(4);
      setCancellationTierValues([
        { hours: '48', percentage: 100 },
        { hours: '24', percentage: 75 },
        { hours: '2', percentage: 50 },
        { hours: '0', percentage: 0 },
      ]);
      setAcceptReschedules(true);
      setRescheduleThreshold('24');
      setMaxReschedules('3');
      setAcceptSeriesCancellation(true);
      setSeriesCancellationTiers(4);
      setSeriesCancellationTierValues([
        { hours: '48', percentage: 100 },
        { hours: '24', percentage: 75 },
        { hours: '2', percentage: 50 },
        { hours: '0', percentage: 0 },
      ]);
      setAcceptSessionCancellation(true);
      setSessionCancellationTiers(4);
      setSessionCancellationTierValues([
        { hours: '48', percentage: 100 },
        { hours: '24', percentage: 75 },
        { hours: '2', percentage: 50 },
        { hours: '0', percentage: 0 },
      ]);
      setMaxSessionsCancellable('3');
      setAcceptRemainingCancellation(true);
      setRemainingCancellationTiers(4);
      setRemainingCancellationTierValues([
        { hours: '48', percentage: 100 },
        { hours: '24', percentage: 75 },
        { hours: '2', percentage: 50 },
        { hours: '0', percentage: 0 },
      ]);
      setAcceptSessionReschedule(true);
      setSessionRescheduleThreshold('24');
      setMaxReschedulesPerSession('2');
      setMaxTotalReschedules('10');
      setShowCustomPolicyForm(false);
    } catch (error: any) {
      showToast(error.message || 'Failed to save policy. Please try again.', 'error');
    }
  };

  const activatePolicy = async (policyId: number) => {
    if (!canEditPolicies) {
      showToast('Only the venue owner can manage policies.', 'error');
      return;
    }
    const confirmed = await confirm({
      title: 'Activate Policy',
      message: 'Are you sure you want to activate this policy? This will deactivate any other active policy for this booking type.',
      confirmLabel: 'Activate',
      cancelLabel: 'Cancel',
      variant: 'warning',
    });

    if (!confirmed) {
      return;
    }

    if (!venue?.id) {
      showToast('Venue ID not found', 'error');
      return;
    }

    try {
      await activatePolicyMutation.mutateAsync({
        venueId: venue.id,
        policyId,
        bookingType: policyBookingType,
      });
      showToast('Policy activated successfully!', 'success');
    } catch (error: any) {
      showToast(error.message || 'Failed to activate policy. Please try again.', 'error');
    }
  };

  const deletePolicy = async (policyId: number) => {
    if (!canEditPolicies) {
      showToast('Only the venue owner can manage policies.', 'error');
      return;
    }
    const confirmed = await confirm({
      title: 'Delete Policy',
      message: 'Are you sure you want to delete this policy? This action cannot be undone.',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    if (!venue?.id) {
      showToast('Venue ID not found', 'error');
      return;
    }

    try {
      await deletePolicyMutation.mutateAsync({
        venueId: venue.id,
        policyId,
        bookingType: policyBookingType,
      });
      showToast('Policy deleted successfully!', 'success');
    } catch (error: any) {
      showToast(error.message || 'Failed to delete policy. Please try again.', 'error');
    }
  };

  // ============================================================================
  // LOADING STATE
  // ============================================================================

  if (isPending) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading venue information...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // ERROR STATE
  // ============================================================================

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Venue Data</h3>
              <p className="text-red-700 mb-4">{error.message}</p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // NO VENUE STATE
  // ============================================================================

  if (!venue) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-700">No venue data found. Please contact support.</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MAIN RENDER - EXACT DESIGN FROM ORIGINAL
  // ============================================================================

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* ====================================================================
            PHASE 2: VENUE DETAILS & IMAGES - EXACT ORIGINAL DESIGN
            ==================================================================== */}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Venue Information</h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Admin Managed
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Basic Info - Left Column */}
            <div className="flex flex-col justify-center space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Venue Name</label>
                <p className="text-gray-900 font-normal text-lg">{venue.name}</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Location</label>
                <div className="flex items-start space-x-2">
                  <MapPin className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                  <p className="text-gray-700 font-normal">{venue.address}, {venue.city}, {venue.state}, {venue.country}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <div>
                  <p className={`text-gray-700 font-normal leading-relaxed ${!isDescriptionExpanded && venue.description && venue.description.length > 150 ? 'line-clamp-3' : ''}`}>
                    {venue.description || 'No description available'}
                  </p>
                  {venue.description && venue.description.length > 150 && (
                    <button
                      onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                      className="text-blue-600 hover:text-blue-700 text-sm font-semibold mt-2 inline-flex items-center transition-colors"
                    >
                      {isDescriptionExpanded ? 'Read less' : 'Read more'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Images - Right Column */}
            <div>
              {(() => {
                const validImages = venue.images?.filter((image: any) => typeof image === 'string' && image.trim() !== '') || [];
                return validImages.length > 0;
              })() ? (
                <div className="space-y-4">
                  {/* Main image display with navigation */}
                  <div
                    className="relative aspect-[16/9] bg-gray-100 rounded-xl overflow-hidden cursor-pointer group"
                    onClick={() => setIsImageModalOpen(true)}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    {venue.images
                      .filter((image: any) => typeof image === 'string' && image.trim() !== '')
                      .map((image: string, index: number) => (
                        <img
                          key={index}
                          src={image}
                          alt={`${venue.name} - Image ${index + 1}`}
                          className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
                            index === currentImageIndex ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                          }`}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ))}

                    {/* Fallback placeholder - behind images */}
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 -z-10">
                      <div className="text-center">
                        <Camera className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Image not available</p>
                      </div>
                    </div>

                    {/* Click to view overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                      <p className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-medium">
                        Click to view full size
                      </p>
                    </div>

                    {/* Navigation arrows for multiple images */}
                    {venue.images.filter((image: any) => typeof image === 'string' && image.trim() !== '').length > 1 && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const validImages = venue.images.filter((img: any) => typeof img === 'string' && img.trim() !== '');
                            setCurrentImageIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
                          }}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full transition-all duration-200"
                        >
                          <ChevronLeft className="h-6 w-6" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const validImages = venue.images.filter((img: any) => typeof img === 'string' && img.trim() !== '');
                            setCurrentImageIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
                          }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full transition-all duration-200"
                        >
                          <ChevronRight className="h-6 w-6" />
                        </button>
                      </>
                    )}

                    {/* Image count indicator */}
                    {venue.images.filter((image: any) => typeof image === 'string' && image.trim() !== '').length > 1 && (
                      <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white px-3 py-1.5 rounded-full text-sm font-medium">
                        {currentImageIndex + 1} / {venue.images.filter((image: any) => typeof image === 'string' && image.trim() !== '').length}
                      </div>
                    )}
                  </div>

                  {/* Info text below image - no thumbnails, just count */}
                  <p className="text-xs text-gray-500 font-normal">
                    {venue.images.filter((img: any) => typeof img === 'string' && img.trim() !== '').length} image{venue.images.filter((img: any) => typeof img === 'string' && img.trim() !== '').length !== 1 ? 's' : ''} uploaded by admin
                  </p>
                </div>
              ) : (
                // Show single placeholder if no images
                <div className="relative aspect-[16/9] bg-gray-100 rounded-xl overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <Camera className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 font-normal">Venue images haven't been set up yet</p>
                      <p className="text-xs text-gray-400 font-normal mt-1">Images will be added by the Spotts admin team</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ====================================================================
            PHASE 3: OPERATING HOURS - EXACT ORIGINAL DESIGN WITH EDIT
            ==================================================================== */}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-blue-500" />
              Operating Hours
            </h3>
            <button
              onClick={() => {
                if (isEditingHours) {
                  resetOperatingHours(); // Reset to original values when cancelling
                }
                setIsEditingHours(!isEditingHours);
              }}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              {isEditingHours ? <X className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
              {isEditingHours ? 'Cancel' : 'Edit'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {operatingHours.map((schedule, index) => (
              <div key={schedule.day} className="flex items-center space-x-4 p-3 border border-gray-200 rounded-lg">
                <div className="w-20">
                  <span className="text-sm font-medium text-gray-700">{schedule.day}</span>
                </div>

                {isEditingHours ? (
                  <div className="flex items-center space-x-2 flex-1">
                    <input
                      type="checkbox"
                      checked={schedule.isOpen}
                      onChange={(e) => {
                        const updated = [...operatingHours];
                        updated[index].isOpen = e.target.checked;
                        setOperatingHours(updated);
                      }}
                      className="rounded border-gray-300"
                    />
                    <span className="text-xs text-gray-500 w-12">
                      {schedule.isOpen ? 'Open' : 'Closed'}
                    </span>
                    {schedule.isOpen && (
                      <>
                        <input
                          type="time"
                          value={schedule.openTime}
                          onChange={(e) => {
                            const updated = [...operatingHours];
                            updated[index].openTime = e.target.value;
                            setOperatingHours(updated);
                          }}
                          className="text-xs border border-gray-300 rounded px-2 py-1"
                        />
                        <span className="text-xs text-gray-500">to</span>
                        <input
                          type="time"
                          value={schedule.closeTime}
                          onChange={(e) => {
                            const updated = [...operatingHours];
                            updated[index].closeTime = e.target.value;
                            setOperatingHours(updated);
                          }}
                          className="text-xs border border-gray-300 rounded px-2 py-1"
                        />
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex-1">
                    {schedule.isOpen ? (
                      <span className="text-sm text-gray-900">
                        {schedule.openTime.substring(0, 5)} - {schedule.closeTime.substring(0, 5)}
                      </span>
                    ) : (
                      <span className="text-sm text-red-600 font-medium">Closed</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {isEditingHours && (
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => {
                  resetOperatingHours(); // Reset to original values
                  setIsEditingHours(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={saveOperatingHours}
                disabled={updateOperatingHoursMutation.isPending}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4" />
                <span>{updateOperatingHoursMutation.isPending ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          )}
        </div>

        {/* ====================================================================
            PHASE 4: SPORTS & PRICING - EXACT ORIGINAL DESIGN
            ==================================================================== */}

        {venue && venue.has_sports_booking !== false && venue.business_model !== 'gym_only' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <DollarSign className="h-5 w-5 mr-2 text-green-500" />
                Pricing Management
              </h3>
              <button
                onClick={() => {
                  if (isEditingPricing) {
                    resetSportRates();
                  }
                  setIsEditingPricing(!isEditingPricing);
                }}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                {isEditingPricing ? <X className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
                {isEditingPricing ? 'Cancel' : 'Edit Pricing'}
              </button>
            </div>

            {!isEditingPricing ? (
              // View Mode - Show sport rates only
              <div className="space-y-4">
                {/* Commission Rate - View Only */}
                <div className="p-4 bg-gray-50 border rounded-lg">
                  <div className="text-sm font-medium text-gray-700">Platform Commission</div>
                  <div className="text-lg font-semibold text-gray-900">{commissionRate}%</div>
                  <p className="text-xs text-gray-500 mt-1">
                    Set by platform admin. Your share: {(100 - parseFloat(commissionRate || '15')).toFixed(2)}%
                  </p>
                </div>

                {/* Sport-Specific Pricing Display */}
                {venue && venue.sports && Array.isArray(venue.sports) && venue.sports.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Sport-Specific Pricing</h4>
                    <div className="space-y-2">
                      {venue.sports.map((sport: any) => {
                        const rate = sportRates[sport.id];
                        if (!rate?.rate) return null;
                        return (
                          <div key={sport.id} className="flex justify-between items-center p-3 border rounded-lg">
                            <span className="text-sm font-medium">{sport.name}</span>
                            <span className="text-sm font-semibold">
                              {CURRENCY_SYMBOL}{parseFloat(rate.rate).toLocaleString()} / {rate.pricing_type === 'hourly' ? 'hour' : 'person'}
                            </span>
                          </div>
                        );
                      })}
                      {Object.keys(sportRates).filter(id => sportRates[parseInt(id)]?.rate).length === 0 && (
                        <p className="text-sm text-gray-500 italic">No sport-specific pricing set. Using venue base rates set by admin.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Edit Mode
              <div className="space-y-6">
                {/* Commission Rate Section - Read Only */}
                <div className="p-4 bg-gray-50 border rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Platform Commission Rate
                  </label>
                  <div className="text-lg font-semibold text-gray-900">{commissionRate}%</div>
                  <p className="text-xs text-gray-500 mt-1">
                    Set by platform admin. Your share: {(100 - parseFloat(commissionRate || '15')).toFixed(2)}%
                  </p>
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-xs text-yellow-700">
                      ⚠️ Commission rates are set by platform administrators during venue creation.
                    </p>
                  </div>
                </div>

                {/* Sport-Specific Pricing */}
                {venue && venue.sports && Array.isArray(venue.sports) && venue.sports.length > 0 && (
                  <div className="border-t pt-4">
                    <div className="mb-3">
                      <h4 className="text-sm font-medium text-gray-900 mb-1">Sport-Specific Pricing</h4>
                      <p className="text-xs text-gray-500">
                        Set custom rates for each sport. Base prices are set by admin during venue creation.
                      </p>
                    </div>
                    <div className="space-y-2">
                      {venue.sports.map((sport) => {
                        const currentRate = sportRates[sport.id];
                        const currentPricingType = currentRate?.pricing_type || 'hourly';

                        return (
                          <div key={sport.id} className="flex items-center justify-between p-3 border rounded-md bg-gray-50">
                            <div className="flex items-center space-x-2">
                              <Tag className="h-4 w-4 text-gray-400" />
                              <span className="text-sm font-medium">{sport.name}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-500">{CURRENCY_SYMBOL}</span>
                              <input
                                type="number"
                                value={currentRate?.rate || ''}
                                onChange={(e) => {
                                  setSportRates({
                                    ...sportRates,
                                    [sport.id]: {
                                      ...sportRates[sport.id],
                                      rate: e.target.value,
                                      pricing_type: currentPricingType
                                    }
                                  });
                                }}
                                placeholder="Enter rate"
                                className="w-28 border border-gray-300 rounded px-2 py-1 text-sm"
                              />
                              <select
                                value={currentPricingType}
                                onChange={(e) => {
                                  setSportRates({
                                    ...sportRates,
                                    [sport.id]: {
                                      ...sportRates[sport.id],
                                      pricing_type: e.target.value as 'hourly' | 'per_person'
                                    }
                                  });
                                }}
                                className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                              >
                                <option value="hourly">per hour</option>
                                <option value="per_person">per person</option>
                              </select>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      💡 Custom rates override the venue base rate for specific sports. Choose between hourly or per-person pricing for each sport.
                    </p>
                  </div>
                )}
              </div>
            )}

            {isEditingPricing && (
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    resetSportRates();
                    setIsEditingPricing(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={savePricing}
                  disabled={isSavingPricing}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4" />
                  <span>{isSavingPricing ? 'Saving...' : 'Save Pricing'}</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* ====================================================================
            PHASE 4.5: SESSION DURATION SETTINGS
            ==================================================================== */}

        {venue && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Info className="h-5 w-5 mr-2 text-blue-600" />
                  Session Duration Limits
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Set minimum and maximum booking duration in hours
                </p>
              </div>

              {!isEditingDuration && (
                <button
                  onClick={() => setIsEditingDuration(true)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                >
                  <Edit className="h-4 w-4" />
                  <span>Edit Duration</span>
                </button>
              )}
            </div>

            {!isEditingDuration ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Minimum Session Duration</p>
                  <p className="text-2xl font-bold text-gray-900">{venue.min_session_hours || 1} hours</p>
                  <p className="text-xs text-gray-500 mt-1">Minimum booking length per session</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Maximum Session Duration</p>
                  <p className="text-2xl font-bold text-gray-900">{venue.max_session_hours ? `${venue.max_session_hours} hours` : 'No limit'}</p>
                  <p className="text-xs text-gray-500 mt-1">Maximum booking length per session</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Min Session Duration */}
                  <div>
                    <label htmlFor="minSessionHours" className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Session Duration (hours) <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="minSessionHours"
                      type="number"
                      min="0.5"
                      max="24"
                      step="0.5"
                      value={minSessionHours}
                      onChange={(e) => setMinSessionHours(Math.max(0.5, Number(e.target.value)))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Minimum booking length (e.g., 1 = 1 hour, 1.5 = 1.5 hours)
                    </p>
                  </div>

                  {/* Max Session Duration */}
                  <div>
                    <label htmlFor="maxSessionHours" className="block text-sm font-medium text-gray-700 mb-2">
                      Maximum Session Duration (hours - Optional)
                    </label>
                    <input
                      id="maxSessionHours"
                      type="number"
                      min={minSessionHours}
                      max="24"
                      step="0.5"
                      value={maxSessionHours || ''}
                      onChange={(e) => setMaxSessionHours(e.target.value ? Number(e.target.value) : null)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Leave empty for no limit"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum booking length per session (leave empty for no limit)
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={saveDurationSettings}
                    disabled={isSavingDuration}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Save className="h-4 w-4" />
                    <span>{isSavingDuration ? 'Saving...' : 'Save Duration'}</span>
                  </button>

                  <button
                    onClick={() => {
                      setIsEditingDuration(false);
                      // Reset to venue values
                      setMinSessionHours(venue.min_session_hours || 1);
                      setMaxSessionHours(venue.max_session_hours || null);
                    }}
                    disabled={isSavingDuration}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ====================================================================
            PHASE 5: DYNAMIC PRICING RULES - INDUSTRY-STANDARD UI
            ==================================================================== */}

        {venue && venue.has_sports_booking !== false && venue.business_model !== 'gym_only' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <TrendingUp className="h-5 w-5 mr-2 text-purple-600" />
                Dynamic Pricing Rules
              </h3>
              <button
                onClick={() => setShowAddPricingRule(true)}
                disabled={!canManagePricing}
                title={canManagePricing ? undefined : "You don't have permission to manage pricing"}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-purple-600"
              >
                <Plus className="h-4 w-4" />
                <span>Add Rule</span>
              </button>
            </div>

            {!canManagePricing && (
              <div className="mb-4 bg-gray-50 border border-gray-200 text-gray-600 text-sm rounded-lg p-3">
                You don't have permission to manage dynamic pricing. Contact the venue owner for access.
              </div>
            )}

            {/* Existing Pricing Rules */}
            <div className="space-y-3 mb-6">
              {isLoadingPricingRules ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-3"></div>
                  <p>Loading pricing rules...</p>
                </div>
              ) : pricingRules.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <TrendingUp className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p>No pricing rules configured yet</p>
                  <p className="text-sm mt-1">Add surcharges for peak hours, weekends, or special dates</p>
                </div>
              ) : (
                pricingRules.map((rule) => (
                  <div key={rule.id} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-medium text-gray-900">{rule.name}</h4>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          rule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
                          {rule.rule_type.replace('_', ' ')}
                        </span>
                      </div>

                      {rule.description && (
                        <p className="text-sm text-gray-600 mb-2">{rule.description}</p>
                      )}

                      <div className="flex items-center space-x-4 text-sm text-gray-700">
                        <span className="font-medium">
                          {rule.adjustment_type === 'fixed_amount'
                            ? `+${CURRENCY_SYMBOL}${Number(rule.adjustment_value).toLocaleString()}/hour`
                            : `+${rule.adjustment_value}%`
                          }
                        </span>

                        {rule.rule_type === 'time_based' && (
                          <span>
                            {rule.applies_from_time} - {rule.applies_to_time || 'end of day'}
                          </span>
                        )}

                        {rule.rule_type === 'day_based' && rule.applies_on_days && (
                          <span>
                            {(() => {
                              try {
                                const days = typeof rule.applies_on_days === 'string'
                                  ? JSON.parse(rule.applies_on_days)
                                  : rule.applies_on_days;
                                return Array.isArray(days)
                                  ? days.map((day: number) =>
                                      ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day]
                                    ).join(', ')
                                  : '';
                              } catch (e) {
                                return '';
                              }
                            })()}
                          </span>
                        )}

                        {rule.rule_type === 'date_range' && (
                          <span>
                            {rule.applies_from_date} to {rule.applies_to_date}
                          </span>
                        )}

                        {rule.court_id && rule.court && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                            Court: {rule.court.name}
                          </span>
                        )}

                        {rule.sport_id && rule.sport && (
                          <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded">
                            Sport: {rule.sport.name}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => togglePricingRule(rule.id, rule.is_active)}
                        disabled={togglePricingRuleMutation.isPending}
                        className={`p-2 rounded-md transition-colors ${
                          rule.is_active
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={rule.is_active ? 'Disable rule' : 'Enable rule'}
                      >
                        <Power className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deletePricingRule(rule.id)}
                        disabled={deletePricingRuleMutation.isPending}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete rule"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Add Rule Form */}
            {showAddPricingRule && (
              <div className="border-t pt-6">
                <h4 className="font-medium text-gray-900 mb-4">Add New Pricing Rule</h4>

                <div className="space-y-4">
                  {/* Rule Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rule Name *
                    </label>
                    <input
                      type="text"
                      value={newRuleName}
                      onChange={(e) => setNewRuleName(e.target.value)}
                      placeholder="e.g., Evening Peak Hours"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Rule Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={newRuleDescription}
                      onChange={(e) => setNewRuleDescription(e.target.value)}
                      placeholder="Optional description for internal reference"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Rule Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rule Type *
                    </label>
                    <select
                      value={newRuleType}
                      onChange={(e) => setNewRuleType(e.target.value as RuleType)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="time_based">Time Based (e.g., after 6pm)</option>
                      <option value="day_based">Day Based (e.g., weekends)</option>
                      <option value="date_range">Date Range (e.g., holidays)</option>
                    </select>
                  </div>

                  {/* Adjustment Type and Value */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Adjustment Type *
                      </label>
                      <select
                        value={newAdjustmentType}
                        onChange={(e) => setNewAdjustmentType(e.target.value as AdjustmentType)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="fixed_amount">Fixed Amount ({CURRENCY_SYMBOL}/hour)</option>
                        <option value="percentage">Percentage (%)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Adjustment Value *
                      </label>
                      <input
                        type="number"
                        value={newAdjustmentValue}
                        onChange={(e) => setNewAdjustmentValue(e.target.value)}
                        placeholder={newAdjustmentType === 'fixed_amount' ? '5000' : '50'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  {/* Time Based Fields */}
                  {newRuleType === 'time_based' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          From Time *
                        </label>
                        <input
                          type="time"
                          value={newAppliesFromTime}
                          onChange={(e) => setNewAppliesFromTime(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          To Time (optional)
                        </label>
                        <input
                          type="time"
                          value={newAppliesToTime}
                          onChange={(e) => setNewAppliesToTime(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  )}

                  {/* Day Based Fields */}
                  {newRuleType === 'day_based' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Applies On Days *
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => {
                              if (newAppliesOnDays.includes(index)) {
                                setNewAppliesOnDays(newAppliesOnDays.filter(d => d !== index));
                              } else {
                                setNewAppliesOnDays([...newAppliesOnDays, index]);
                              }
                            }}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                              newAppliesOnDays.includes(index)
                                ? 'bg-purple-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Date Range Fields */}
                  {newRuleType === 'date_range' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          From Date *
                        </label>
                        <input
                          type="date"
                          value={newAppliesFromDate}
                          onChange={(e) => setNewAppliesFromDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          To Date *
                        </label>
                        <input
                          type="date"
                          value={newAppliesToDate}
                          onChange={(e) => setNewAppliesToDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  )}

                  {/* Optional Filters */}
                  <div className="border-t pt-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-3">Optional Filters</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Apply to Specific Court
                        </label>
                        <select
                          value={newRuleCourtId || ''}
                          onChange={(e) => setNewRuleCourtId(e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">All Courts</option>
                          {venueCourts.map((court: any) => (
                            <option key={court.id} value={court.id}>
                              {court.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Apply to Specific Sport
                        </label>
                        <select
                          value={newRuleSportId || ''}
                          onChange={(e) => setNewRuleSportId(e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">All Sports</option>
                          {venue?.sports?.map((sport: any) => (
                            <option key={sport.id} value={sport.id}>
                              {sport.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority (higher = applied first)
                    </label>
                    <input
                      type="number"
                      value={newPriority}
                      onChange={(e) => setNewPriority(e.target.value)}
                      placeholder="10"
                      min="0"
                      max="100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={addPricingRule}
                      disabled={createPricingRuleMutation.isPending}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="h-4 w-4" />
                      <span>{createPricingRuleMutation.isPending ? 'Adding...' : 'Add Pricing Rule'}</span>
                    </button>
                    <button
                      onClick={resetPricingRuleForm}
                      disabled={createPricingRuleMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Help Text */}
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-start">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">How Dynamic Pricing Works:</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-700">
                    <li>Create rules to add surcharges during peak hours, weekends, or special dates</li>
                    <li>Multiple rules can apply at once - they stack in priority order</li>
                    <li>Customers see pricing info before booking with clear explanations</li>
                    <li>Prices update in real-time as customers select different times</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ====================================================================
            PHASE 6: BLACKOUT DATES - EXACT ORIGINAL DESIGN
            ==================================================================== */}

        {venue && venue.has_sports_booking !== false && venue.business_model !== 'gym_only' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-red-500" />
                Temporary Closures
              </h3>
              <button
                onClick={() => setShowAddBlackout(true)}
                disabled={!canManageSlots}
                title={canManageSlots ? undefined : "You don't have permission to manage temporary closures"}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-red-600"
              >
                <Calendar className="h-4 w-4" />
                Add Blackout Date
              </button>
            </div>

            {!canManageSlots && (
              <div className="mb-4 bg-gray-50 border border-gray-200 text-gray-600 text-sm rounded-lg p-3">
                You don't have permission to manage temporary closures. Contact the venue owner for access.
              </div>
            )}

            {/* Add new blackout form */}
            {showAddBlackout && (() => {
              console.log('🔴 Blackout Form Rendered - Sport Dropdown Check:', {
                hasVenue: !!venue,
                hasSports: !!venue?.sports,
                isArray: Array.isArray(venue?.sports),
                sportsLength: venue?.sports?.length,
                shouldShowDropdown: venue && venue.sports && Array.isArray(venue.sports) && venue.sports.length > 1,
                sports: venue?.sports?.map((s: any) => s.name)
              });
              return null;
            })()}
            {showAddBlackout && (
              <div className="mb-6 p-4 border border-red-200 bg-red-50 rounded-lg">
                <h4 className="text-sm font-medium text-red-900 mb-3">Add Temporary Closure</h4>

                {/* Date Range Toggle */}
                <div className="mb-4">
                  <label className="flex items-center space-x-2 text-sm text-red-700">
                    <input
                      type="checkbox"
                      checked={isDateRange}
                      onChange={(e) => setIsDateRange(e.target.checked)}
                      className="w-4 h-4 text-red-600 border-red-300 rounded focus:ring-red-500"
                    />
                    <span>Multiple days (date range)</span>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-red-700 mb-1">
                      {isDateRange ? 'Start Date' : 'Date'}
                    </label>
                    <input
                      type="date"
                      value={newBlackoutDate}
                      onChange={(e) => setNewBlackoutDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full text-sm border border-red-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  {isDateRange && (
                    <div>
                      <label className="block text-xs text-red-700 mb-1">End Date</label>
                      <input
                        type="date"
                        value={newBlackoutEndDate}
                        onChange={(e) => setNewBlackoutEndDate(e.target.value)}
                        min={newBlackoutDate || new Date().toISOString().split('T')[0]}
                        className="w-full text-sm border border-red-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                  )}

                  <div className={isDateRange ? "md:col-span-2" : ""}>
                    <label className="block text-xs text-red-700 mb-1">Reason for Closure</label>
                    <select
                      value={newBlackoutReason}
                      onChange={(e) => setNewBlackoutReason(e.target.value)}
                      className="w-full text-sm border border-red-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <option value="">Select a reason</option>
                      <option value="Maintenance">Maintenance</option>
                      <option value="Private Event">Private Event</option>
                      <option value="Tournament">Tournament</option>
                      <option value="Holiday">Holiday</option>
                      <option value="Renovation">Renovation</option>
                      <option value="Weather">Weather</option>
                      <option value="Staff Training">Staff Training</option>
                    </select>
                  </div>

                  {/* Sport Selector - Only show if venue has multiple sports */}
                  {venue && venue.sports && Array.isArray(venue.sports) && venue.sports.length > 1 && (
                    <div className={isDateRange ? "md:col-span-2" : ""}>
                      <label className="block text-xs text-red-700 mb-1">
                        Applies To <span className="text-red-400">(Optional)</span>
                      </label>
                      <select
                        value={newBlackoutSportId || ''}
                        onChange={(e) => setNewBlackoutSportId(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full text-sm border border-red-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        <option value="">All Sports (Entire Venue)</option>
                        {venue.sports.map((sport: any) => (
                          <option key={sport.id} value={sport.id}>{sport.name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-red-500 mt-1">
                        Select a specific sport to close only that facility, or leave as "All Sports" to close the entire venue.
                      </p>
                    </div>
                  )}
                </div>

                {/* Preview for date range */}
                {isDateRange && newBlackoutDate && newBlackoutEndDate && (
                  <div className="mt-3 p-2 bg-red-100 border border-red-200 rounded text-sm text-red-700">
                    <strong>Preview:</strong> Closure from {new Date(newBlackoutDate).toLocaleDateString()} to {new Date(newBlackoutEndDate).toLocaleDateString()}
                    {' '}({Math.ceil((new Date(newBlackoutEndDate).getTime() - new Date(newBlackoutDate).getTime()) / (1000 * 60 * 60 * 24)) + 1} days)
                  </div>
                )}

                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    onClick={() => {
                      setShowAddBlackout(false);
                      setNewBlackoutDate('');
                      setNewBlackoutEndDate('');
                      setNewBlackoutReason('');
                      setIsDateRange(false);
                    }}
                    className="px-3 py-2 text-sm text-red-700 bg-white border border-red-300 rounded hover:bg-red-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addBlackoutDate}
                    disabled={!newBlackoutDate || !newBlackoutReason || (isDateRange && !newBlackoutEndDate)}
                    className="px-3 py-2 text-sm text-white bg-red-600 rounded hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isDateRange ? 'Add Date Range' : 'Add Closure'}
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {blackoutDates.map((blackout, index) => (
                <div key={index} className="flex items-center justify-between p-3 border border-red-200 bg-red-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-red-900">
                        {new Date(blackout.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                      {blackout.applies_to && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          blackout.sport_id
                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                            : 'bg-gray-100 text-gray-700 border border-gray-300'
                        }`}>
                          {blackout.applies_to}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-red-700 mt-1">{blackout.reason}</p>
                  </div>
                  <button
                    onClick={() => removeBlackoutDate(index)}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Remove closure"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {blackoutDates.length === 0 && (
                <p className="text-center text-gray-500 py-8">No temporary closures scheduled</p>
              )}
            </div>
          </div>
        )}

        {/* ====================================================================
            PHASE 7: CANCELLATION & REFUND POLICIES - COMPLETE FORM
            ==================================================================== */}

        {/* Cancellation Policy Management */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Shield className="h-5 w-5 mr-2 text-blue-500" />
                Cancellation & Refund Policies
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Set how refunds and cancellations are handled for your venue
              </p>
            </div>
            <button
              onClick={() => setShowPolicySection(!showPolicySection)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              {showPolicySection ? 'Hide' : !canEditPolicies ? 'View Policies' : 'Manage Policies'}
            </button>
          </div>

          {/* Read-only banner — either the account-manager lock, or a staff
              member without policies.manage (owner-only). */}
          {!canEditPolicies && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div>
                {venue?.policy_editing_enabled === false ? (
                  <>
                    <p className="text-sm font-semibold text-amber-900">Policy Editing Locked</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Your account manager has locked policy editing for this venue. You can view your current policies but cannot create, edit, or delete them. Contact Spotts to unlock.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-amber-900">View Only</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      You can view the venue's policies but only the venue owner can create, edit, or activate them.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {showPolicySection && (
            <div className="space-y-6">
              {/* Booking Type Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Policy For:
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPolicyBookingType('one_time')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      policyBookingType === 'one_time'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    One-Time Bookings
                  </button>
                  <button
                    onClick={() => setPolicyBookingType('recurring')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      policyBookingType === 'recurring'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Recurring Bookings
                  </button>
                  {venue?.has_gym_membership && (
                    <button
                      onClick={() => setPolicyBookingType('membership')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        policyBookingType === 'membership'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Memberships
                    </button>
                  )}
                </div>
              </div>

              {/* Active Policy Display */}
              {activePolicy ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <h4 className="font-semibold text-green-900">Active Policy: {activePolicy.name}</h4>
                      </div>

                      {/* Policy display — branched by booking type */}
                      {policyBookingType === 'recurring' ? (
                        /* ── RECURRING policy display ── */
                        <div className="space-y-3 text-sm">
                          {/* Series cancellation */}
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Full Series Cancellation</p>
                            {activePolicy.allow_series_cancellation === false ? (
                              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <span className="font-medium text-red-800">No Refunds</span>
                                <p className="text-red-600 mt-0.5">Cancelling the full series gives no refund.</p>
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {(activePolicy.series_tier_1_hours ?? 0) > 0 && (
                                  <div className="bg-green-100 px-3 py-1.5 rounded-lg">
                                    <span className="font-medium text-green-800">Refund eligible</span>
                                    <span className="text-green-600 ml-1">(cancel {activePolicy.series_tier_1_hours}h+ before first session)</span>
                                  </div>
                                )}
                                {(activePolicy.series_tier_2_hours ?? 0) > 0 && (
                                  <div className="bg-yellow-100 px-3 py-1.5 rounded-lg">
                                    <span className="font-medium text-yellow-800">Partial refund</span>
                                    <span className="text-yellow-600 ml-1">(cancel {activePolicy.series_tier_2_hours}h+ before first session)</span>
                                  </div>
                                )}
                                {(activePolicy.series_tier_3_hours ?? 0) > 0 && (
                                  <div className="bg-orange-100 px-3 py-1.5 rounded-lg">
                                    <span className="font-medium text-orange-800">Minimum refund</span>
                                    <span className="text-orange-600 ml-1">(cancel {activePolicy.series_tier_3_hours}h+ before first session)</span>
                                  </div>
                                )}
                                <div className="bg-red-100 px-3 py-1.5 rounded-lg">
                                  <span className="font-medium text-red-800">No refund</span>
                                  <span className="text-red-600 ml-1">(less than threshold)</span>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Session / individual cancellation */}
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Individual Session Cancellation</p>
                            {activePolicy.allow_session_cancellation === false ? (
                              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <span className="font-medium text-red-800">Not Allowed</span>
                                <p className="text-red-600 mt-0.5">Customers cannot cancel individual sessions.</p>
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                <div className="bg-blue-50 px-3 py-1.5 rounded-lg">
                                  <span className="text-blue-700">Slot freed, no monetary refund</span>
                                </div>
                                {(activePolicy.max_sessions_cancellable ?? 0) > 0 && (
                                  <div className="bg-blue-50 px-3 py-1.5 rounded-lg">
                                    <span className="text-blue-700">Max {activePolicy.max_sessions_cancellable} sessions</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Reschedule info */}
                          <div className="flex flex-wrap gap-4 pt-2 border-t border-green-200">
                            <div>
                              <span className="text-gray-600">Reschedules:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                {activePolicy.allow_reschedule ? `${activePolicy.max_reschedule_count} times` : 'Not allowed'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* ── ONE-TIME / MEMBERSHIP policy display ── */
                        activePolicy.accept_cancellations === false ? (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                            <span className="font-medium text-red-800">No Refund Policy</span>
                            <p className="text-red-600 mt-1">Cancellations are not accepted for refunds.</p>
                          </div>
                        ) : (
                          <div className="space-y-2 text-sm">
                            <div className="flex flex-wrap gap-2">
                              {(activePolicy.tier_1_hours || activePolicy.full_refund_hours) && (
                                <div className="bg-green-100 px-3 py-1.5 rounded-lg">
                                  <span className="font-medium text-green-800">{activePolicy.tier_1_percentage ?? 100}% refund</span>
                                  <span className="text-green-600 ml-1">({activePolicy.tier_1_hours || activePolicy.full_refund_hours}h+ before)</span>
                                </div>
                              )}
                              {((activePolicy.tier_2_hours ?? 0) > 0 || (activePolicy.partial_refund_hours ?? 0) > 0) && (
                                <div className="bg-yellow-100 px-3 py-1.5 rounded-lg">
                                  <span className="font-medium text-yellow-800">{activePolicy.tier_2_percentage ?? activePolicy.partial_refund_percentage ?? 75}% refund</span>
                                  <span className="text-yellow-600 ml-1">({activePolicy.tier_2_hours || activePolicy.partial_refund_hours}h+ before)</span>
                                </div>
                              )}
                              {(activePolicy.tier_3_hours ?? 0) > 0 && (
                                <div className="bg-orange-100 px-3 py-1.5 rounded-lg">
                                  <span className="font-medium text-orange-800">{activePolicy.tier_3_percentage ?? 50}% refund</span>
                                  <span className="text-orange-600 ml-1">({activePolicy.tier_3_hours}h+ before)</span>
                                </div>
                              )}
                              <div className="bg-red-100 px-3 py-1.5 rounded-lg">
                                <span className="font-medium text-red-800">0% refund</span>
                                <span className="text-red-600 ml-1">(less than threshold)</span>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-4 mt-3 pt-3 border-t border-green-200">
                              <div>
                                <span className="text-gray-600">Max Reschedules:</span>
                                <span className="ml-2 font-medium text-gray-900">
                                  {activePolicy.allow_reschedule ? `${activePolicy.max_reschedule_count} times` : 'Not allowed'}
                                </span>
                              </div>
                              {(activePolicy.reschedule_threshold ?? 0) > 0 && (
                                <div>
                                  <span className="text-gray-600">Reschedule Deadline:</span>
                                  <span className="ml-2 font-medium text-gray-900">{activePolicy.reschedule_threshold}h before</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                    {canEditPolicies && (
                      <div className="flex-shrink-0">
                        <button
                          onClick={() => {
                            // Pre-populate form with current policy values
                            setAcceptCancellations(activePolicy.accept_cancellations !== false);
                            setAcceptReschedules(activePolicy.allow_reschedule ?? true);
                            setMaxReschedules(String(activePolicy.max_reschedule_count ?? 3));
                            setRescheduleThreshold(String(activePolicy.reschedule_threshold ?? 24));

                            // Determine number of tiers based on data
                            const hasTier3 = (activePolicy.tier_3_hours ?? 0) > 0;
                            const hasTier2 = (activePolicy.tier_2_hours ?? 0) > 0 || (activePolicy.partial_refund_hours ?? 0) > 0;
                            setCancellationTiers(hasTier3 ? 3 : hasTier2 ? 2 : 2);

                            // Set tier values
                            setCancellationTierValues([
                              { hours: String(activePolicy.tier_1_hours || activePolicy.full_refund_hours || 48), percentage: activePolicy.tier_1_percentage ?? 100 },
                              { hours: String(activePolicy.tier_2_hours || activePolicy.partial_refund_hours || 24), percentage: activePolicy.tier_2_percentage ?? activePolicy.partial_refund_percentage ?? 75 },
                              { hours: String(activePolicy.tier_3_hours || 2), percentage: activePolicy.tier_3_percentage ?? 50 },
                            ]);

                            setShowCustomPolicyForm(true);
                          }}
                          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                        >
                          Edit Policy
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 text-sm">
                    ⚠️ No active policy set. Using platform default (Standard policy).
                  </p>
                </div>
              )}

              {/* Custom Policy Form */}
              {loadingPolicies ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2 text-sm">Loading policies...</p>
                </div>
              ) : (
                <>
                  <div>
                    {!showCustomPolicyForm && !activePolicy && canEditPolicies ? (
                      <button
                        onClick={() => setShowCustomPolicyForm(true)}
                        className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-medium flex items-center justify-center gap-2"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Set Up Policy
                      </button>
                    ) : showCustomPolicyForm ? (
                      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="font-semibold text-gray-900 text-lg">
                              {policyBookingType === 'one_time' ? 'One-Time Booking' : policyBookingType === 'recurring' ? 'Recurring Booking' : 'Membership'} Policy
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">Configure your cancellation and reschedule rules</p>
                          </div>
                          <button
                            onClick={() => setShowCustomPolicyForm(false)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>

                        {/* ONE-TIME BOOKING: Cancellation Questions (only for one-time) */}
                        {policyBookingType === 'one_time' && (
                          <div className="space-y-3">
                            <label className="block text-base font-semibold text-gray-900">
                              1. Do you accept cancellations with refunds?
                            </label>
                            <div className="flex gap-3">
                              <button
                                onClick={() => setAcceptCancellations(true)}
                                className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                  acceptCancellations
                                    ? 'border-green-500 bg-green-50 text-green-700'
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                }`}
                              >
                                ✓ Yes
                              </button>
                              <button
                                onClick={() => setAcceptCancellations(false)}
                                className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                  !acceptCancellations
                                    ? 'border-red-500 bg-red-50 text-red-700'
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                }`}
                              >
                                ✕ No
                              </button>
                            </div>
                          </div>
                        )}

                        {policyBookingType === 'one_time' && acceptCancellations && (
                          <>
                            <div className="space-y-3 border-t pt-6">
                              <label className="block text-base font-semibold text-gray-900">
                                2. How many refund tiers do you want?
                              </label>
                              <p className="text-sm text-gray-600">Choose how flexible your refund policy should be</p>
                              <div className="grid grid-cols-3 sm:grid-cols-3 gap-3">
                                {[2, 3, 4].map((tierCount) => (
                                  <button
                                    key={tierCount}
                                    onClick={() => setCancellationTiers(tierCount as 2 | 3 | 4)}
                                    className={`px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                      cancellationTiers === tierCount
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}
                                  >
                                    {tierCount} Tiers
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="space-y-4 border-t pt-6">
                              <label className="block text-base font-semibold text-gray-900">
                                3. Set your refund tiers
                              </label>
                              <p className="text-sm text-gray-600">Customers get refunds based on how far in advance they cancel</p>
                              <div className="space-y-3">
                                {Array.from({ length: cancellationTiers }).map((_, index) => (
                                  <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                                    <div className="flex items-center gap-3">
                                      <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold">
                                        {index + 1}
                                      </div>
                                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Hours Before Booking
                                          </label>
                                          <input
                                            type="number"
                                            value={cancellationTierValues[index]?.hours || ''}
                                            onChange={(e) => {
                                              const newTiers = [...cancellationTierValues];
                                              newTiers[index] = { ...newTiers[index], hours: e.target.value };
                                              setCancellationTierValues(newTiers);
                                            }}
                                            placeholder="e.g., 48"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Refund Percentage
                                          </label>
                                          <input
                                            type="number"
                                            value={cancellationTierValues[index]?.percentage || ''}
                                            onChange={(e) => {
                                              const newTiers = [...cancellationTierValues];
                                              newTiers[index] = { ...newTiers[index], percentage: parseInt(e.target.value) };
                                              setCancellationTierValues(newTiers);
                                            }}
                                            placeholder="e.g., 100"
                                            min="0"
                                            max="100"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    <p className="text-xs text-gray-600 mt-2 ml-13">
                                      If cancelled ≥ {cancellationTierValues[index]?.hours || '___'} hours before booking → {cancellationTierValues[index]?.percentage || '__'}% refund
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </>
                        )}

                        {policyBookingType === 'one_time' && (
                          <div className="space-y-3 border-t pt-6">
                            <label className="block text-base font-semibold text-gray-900">
                              {acceptCancellations ? '4' : '2'}. Do you allow reschedules?
                            </label>
                            <div className="flex gap-3">
                              <button
                                onClick={() => setAcceptReschedules(true)}
                                className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                  acceptReschedules
                                    ? 'border-green-500 bg-green-50 text-green-700'
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                }`}
                              >
                                ✓ Yes
                              </button>
                              <button
                                onClick={() => setAcceptReschedules(false)}
                                className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                  !acceptReschedules
                                    ? 'border-red-500 bg-red-50 text-red-700'
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                }`}
                              >
                                ✕ No
                              </button>
                            </div>
                          </div>
                        )}

                        {policyBookingType === 'one_time' && acceptReschedules && (
                          <>
                            <div className="space-y-3 border-t pt-6">
                              <label className="block text-base font-semibold text-gray-900">
                                {acceptCancellations ? '5' : '3'}. How many hours before booking can they reschedule?
                              </label>
                              <p className="text-sm text-gray-600">Set the minimum notice period required for rescheduling</p>
                              <div className="flex items-center gap-3">
                                <input
                                  type="number"
                                  value={rescheduleThreshold}
                                  onChange={(e) => setRescheduleThreshold(e.target.value)}
                                  min="0"
                                  max="168"
                                  placeholder="e.g., 24"
                                  className="w-32 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-semibold text-center"
                                />
                                <span className="text-gray-600">hours before</span>
                              </div>
                            </div>

                            <div className="space-y-3 border-t pt-6">
                              <label className="block text-base font-semibold text-gray-900">
                                {acceptCancellations ? '6' : '4'}. How many times can customers reschedule?
                              </label>
                              <div className="flex items-center gap-3">
                                <input
                                  type="number"
                                  value={maxReschedules}
                                  onChange={(e) => setMaxReschedules(e.target.value)}
                                  min="0"
                                  max="10"
                                  className="w-32 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-semibold text-center"
                                />
                                <span className="text-gray-600">times</span>
                              </div>
                              <p className="text-sm text-gray-500">Customers can reschedule their booking up to this many times</p>
                            </div>
                          </>
                        )}

                        {/* RECURRING BOOKING POLICIES - SIMPLIFIED */}
                        {policyBookingType === 'recurring' && (
                          <>
                            {/* SECTION 1: Refund Policy */}
                            <div className="space-y-4 border-t pt-6">
                              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
                                <h4 className="font-semibold text-purple-900 mb-2">💰 SECTION 1: Refund Policy</h4>
                                <p className="text-sm text-purple-700">Set refund tiers for full series cancellations (hours before first session)</p>
                                <p className="text-xs text-purple-600 mt-1">• Applies when customer cancels the ENTIRE recurring booking before it starts</p>
                                <p className="text-xs text-orange-600 mt-1 font-medium">⚠️ Individual session cancellations are non-refundable (slot is freed for others to book)</p>
                              </div>

                              <div className="space-y-3">
                                <label className="block text-base font-semibold text-gray-900">
                                  Do you give refunds when customers cancel?
                                </label>
                                <div className="flex gap-3">
                                  <button
                                    onClick={() => setAcceptSeriesCancellation(true)}
                                    className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                      acceptSeriesCancellation
                                        ? 'border-green-500 bg-green-50 text-green-700'
                                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}
                                  >
                                    ✓ Yes
                                  </button>
                                  <button
                                    onClick={() => setAcceptSeriesCancellation(false)}
                                    className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                      !acceptSeriesCancellation
                                        ? 'border-red-500 bg-red-50 text-red-700'
                                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}
                                  >
                                    ✕ No
                                  </button>
                                </div>
                              </div>

                              {acceptSeriesCancellation && (
                                <>
                                  <div className="space-y-3">
                                    <label className="block text-base font-semibold text-gray-900">
                                      How many refund tiers?
                                    </label>
                                    <div className="grid grid-cols-3 sm:grid-cols-3 gap-3">
                                      {[2, 3, 4].map((tierCount) => (
                                        <button
                                          key={tierCount}
                                          onClick={() => setSeriesCancellationTiers(tierCount as 2 | 3 | 4)}
                                          className={`px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                            seriesCancellationTiers === tierCount
                                              ? 'border-purple-500 bg-purple-50 text-purple-700'
                                              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                          }`}
                                        >
                                          {tierCount} Tiers
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  <div className="space-y-3">
                                    <label className="block text-base font-semibold text-gray-900">
                                      Set refund tiers (hours before FIRST session)
                                    </label>
                                    {Array.from({ length: seriesCancellationTiers }).map((_, index) => (
                                      <div key={index} className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
                                        <div className="flex items-center gap-3">
                                          <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-semibold">
                                            {index + 1}
                                          </div>
                                          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Hours Before Session
                                              </label>
                                              <input
                                                type="number"
                                                value={seriesCancellationTierValues[index]?.hours || ''}
                                                onChange={(e) => {
                                                  const newTiers = [...seriesCancellationTierValues];
                                                  newTiers[index] = { ...newTiers[index], hours: e.target.value };
                                                  setSeriesCancellationTierValues(newTiers);
                                                }}
                                                placeholder="e.g., 48"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                                              />
                                            </div>
                                            <div>
                                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Refund Percentage
                                              </label>
                                              <input
                                                type="number"
                                                value={seriesCancellationTierValues[index]?.percentage || ''}
                                                onChange={(e) => {
                                                  const newTiers = [...seriesCancellationTierValues];
                                                  newTiers[index] = { ...newTiers[index], percentage: parseInt(e.target.value) };
                                                  setSeriesCancellationTierValues(newTiers);
                                                }}
                                                placeholder="e.g., 100"
                                                min="0"
                                                max="100"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                                              />
                                            </div>
                                          </div>
                                        </div>
                                        <p className="text-xs text-gray-600 mt-2 ml-13">
                                          If cancelled ≥ {seriesCancellationTierValues[index]?.hours || '___'} hours before session → {seriesCancellationTierValues[index]?.percentage || '__'}% refund
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>

                            {/*
                            =======================================================================
                            COMMENTED OUT: Section 2 - Individual Session Cancellation
                            Reason: Uses same refund tiers as Section 1 - no separate config needed.
                            Individual session cancellation is automatically enabled with Section 1.
                            =======================================================================
                            <div className="space-y-4 border-t pt-6">
                              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="font-semibold text-blue-900 mb-2">🎯 SECTION 2: Individual Session Cancellation</h4>
                                <p className="text-sm text-blue-700">Allow customers to cancel specific sessions within the series</p>
                              </div>

                              <div className="space-y-3">
                                <label className="block text-base font-semibold text-gray-900">
                                  Allow customers to cancel individual sessions?
                                </label>
                                <div className="flex gap-3">
                                  <button
                                    onClick={() => setAcceptSessionCancellation(true)}
                                    className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                      acceptSessionCancellation
                                        ? 'border-green-500 bg-green-50 text-green-700'
                                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}
                                  >
                                    ✓ Yes
                                  </button>
                                  <button
                                    onClick={() => setAcceptSessionCancellation(false)}
                                    className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                      !acceptSessionCancellation
                                        ? 'border-red-500 bg-red-50 text-red-700'
                                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}
                                  >
                                    ✕ No
                                  </button>
                                </div>
                              </div>

                              {acceptSessionCancellation && (
                                <>
                                  <div className="space-y-3">
                                    <label className="block text-base font-semibold text-gray-900">
                                      How many refund tiers?
                                    </label>
                                    <div className="grid grid-cols-3 sm:grid-cols-3 gap-3">
                                      {[2, 3, 4].map((tierCount) => (
                                        <button
                                          key={tierCount}
                                          onClick={() => setSessionCancellationTiers(tierCount as 2 | 3 | 4)}
                                          className={`px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                            sessionCancellationTiers === tierCount
                                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                                              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                          }`}
                                        >
                                          {tierCount} Tiers
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  <div className="space-y-3">
                                    <label className="block text-base font-semibold text-gray-900">
                                      Set refund tiers (hours before THAT session)
                                    </label>
                                    {Array.from({ length: sessionCancellationTiers }).map((_, index) => (
                                      <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                                        <div className="flex items-center gap-3">
                                          <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold">
                                            {index + 1}
                                          </div>
                                          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Hours Before Session
                                              </label>
                                              <input
                                                type="number"
                                                value={sessionCancellationTierValues[index]?.hours || ''}
                                                onChange={(e) => {
                                                  const newTiers = [...sessionCancellationTierValues];
                                                  newTiers[index] = { ...newTiers[index], hours: e.target.value };
                                                  setSessionCancellationTierValues(newTiers);
                                                }}
                                                placeholder="e.g., 48"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                              />
                                            </div>
                                            <div>
                                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Refund Percentage
                                              </label>
                                              <input
                                                type="number"
                                                value={sessionCancellationTierValues[index]?.percentage || ''}
                                                onChange={(e) => {
                                                  const newTiers = [...sessionCancellationTierValues];
                                                  newTiers[index] = { ...newTiers[index], percentage: parseInt(e.target.value) };
                                                  setSessionCancellationTierValues(newTiers);
                                                }}
                                                placeholder="e.g., 100"
                                                min="0"
                                                max="100"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                              />
                                            </div>
                                          </div>
                                        </div>
                                        <p className="text-xs text-gray-600 mt-2 ml-13">
                                          If cancelled ≥ {sessionCancellationTierValues[index]?.hours || '___'} hours before session → {sessionCancellationTierValues[index]?.percentage || '__'}% refund
                                        </p>
                                      </div>
                                    ))}
                                  </div>

                                  <div className="space-y-3">
                                    <label className="block text-base font-semibold text-gray-900">
                                      Max sessions cancellable (total)
                                    </label>
                                    <div className="flex items-center gap-3">
                                      <input
                                        type="number"
                                        value={maxSessionsCancellable}
                                        onChange={(e) => setMaxSessionsCancellable(e.target.value)}
                                        min="0"
                                        max="100"
                                        className="w-32 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-semibold text-center"
                                      />
                                      <span className="text-gray-600">sessions</span>
                                    </div>
                                    <p className="text-sm text-gray-500">Total number of sessions customer can cancel from the entire series</p>
                                  </div>
                                </>
                              )}
                            </div>
                            */}

                            {/*
                            =======================================================================
                            COMMENTED OUT: Section 3 - Remaining Sessions Cancellation
                            Reason: Not needed - once first session starts, no refunds.
                            Customers can cancel full series or individual sessions, not "remaining".
                            =======================================================================
                            <div className="space-y-4 border-t pt-6">
                              <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
                                <h4 className="font-semibold text-orange-900 mb-2">⏭️ SECTION 3: Remaining Sessions Cancellation</h4>
                                <p className="text-sm text-orange-700">Allow customers to cancel all future sessions at once</p>
                              </div>

                              <div className="space-y-3">
                                <label className="block text-base font-semibold text-gray-900">
                                  Allow customers to cancel all remaining sessions?
                                </label>
                                <div className="flex gap-3">
                                  <button
                                    onClick={() => setAcceptRemainingCancellation(true)}
                                    className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                      acceptRemainingCancellation
                                        ? 'border-green-500 bg-green-50 text-green-700'
                                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}
                                  >
                                    ✓ Yes
                                  </button>
                                  <button
                                    onClick={() => setAcceptRemainingCancellation(false)}
                                    className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                      !acceptRemainingCancellation
                                        ? 'border-red-500 bg-red-50 text-red-700'
                                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}
                                  >
                                    ✕ No
                                  </button>
                                </div>
                              </div>

                              {acceptRemainingCancellation && (
                                <>
                                  <div className="space-y-3">
                                    <label className="block text-base font-semibold text-gray-900">
                                      How many refund tiers?
                                    </label>
                                    <div className="grid grid-cols-3 sm:grid-cols-3 gap-3">
                                      {[2, 3, 4].map((tierCount) => (
                                        <button
                                          key={tierCount}
                                          onClick={() => setRemainingCancellationTiers(tierCount as 2 | 3 | 4)}
                                          className={`px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                            remainingCancellationTiers === tierCount
                                              ? 'border-orange-500 bg-orange-50 text-orange-700'
                                              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                          }`}
                                        >
                                          {tierCount} Tiers
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  <div className="space-y-3">
                                    <label className="block text-base font-semibold text-gray-900">
                                      Set refund tiers (hours before NEXT session)
                                    </label>
                                    {Array.from({ length: remainingCancellationTiers }).map((_, index) => (
                                      <div key={index} className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
                                        <div className="flex items-center gap-3">
                                          <div className="flex-shrink-0 w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center font-semibold">
                                            {index + 1}
                                          </div>
                                          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Hours Before Next Session
                                              </label>
                                              <input
                                                type="number"
                                                value={remainingCancellationTierValues[index]?.hours || ''}
                                                onChange={(e) => {
                                                  const newTiers = [...remainingCancellationTierValues];
                                                  newTiers[index] = { ...newTiers[index], hours: e.target.value };
                                                  setRemainingCancellationTierValues(newTiers);
                                                }}
                                                placeholder="e.g., 48"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                                              />
                                            </div>
                                            <div>
                                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Refund Percentage
                                              </label>
                                              <input
                                                type="number"
                                                value={remainingCancellationTierValues[index]?.percentage || ''}
                                                onChange={(e) => {
                                                  const newTiers = [...remainingCancellationTierValues];
                                                  newTiers[index] = { ...newTiers[index], percentage: parseInt(e.target.value) };
                                                  setRemainingCancellationTierValues(newTiers);
                                                }}
                                                placeholder="e.g., 100"
                                                min="0"
                                                max="100"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                                              />
                                            </div>
                                          </div>
                                        </div>
                                        <p className="text-xs text-gray-600 mt-2 ml-13">
                                          If cancelled ≥ {remainingCancellationTierValues[index]?.hours || '___'} hours before next session → {remainingCancellationTierValues[index]?.percentage || '__'}% refund
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                            */}

                            {/* SECTION 2: Session Reschedule */}
                            <div className="space-y-4 border-t pt-6">
                              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                                <h4 className="font-semibold text-green-900 mb-2">🔄 SECTION 2: Session Reschedule</h4>
                                <p className="text-sm text-green-700">Allow customers to reschedule individual sessions</p>
                              </div>

                              <div className="space-y-3">
                                <label className="block text-base font-semibold text-gray-900">
                                  Allow customers to reschedule individual sessions?
                                </label>
                                <div className="flex gap-3">
                                  <button
                                    onClick={() => setAcceptSessionReschedule(true)}
                                    className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                      acceptSessionReschedule
                                        ? 'border-green-500 bg-green-50 text-green-700'
                                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}
                                  >
                                    ✓ Yes
                                  </button>
                                  <button
                                    onClick={() => setAcceptSessionReschedule(false)}
                                    className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                                      !acceptSessionReschedule
                                        ? 'border-red-500 bg-red-50 text-red-700'
                                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}
                                  >
                                    ✕ No
                                  </button>
                                </div>
                              </div>

                              {acceptSessionReschedule && (
                                <>
                                  <div className="space-y-3">
                                    <label className="block text-base font-semibold text-gray-900">
                                      Reschedule threshold (hours before session)
                                    </label>
                                    <p className="text-sm text-gray-600">Minimum notice period required to reschedule a session</p>
                                    <div className="flex items-center gap-3">
                                      <input
                                        type="number"
                                        value={sessionRescheduleThreshold}
                                        onChange={(e) => setSessionRescheduleThreshold(e.target.value)}
                                        min="0"
                                        max="168"
                                        placeholder="e.g., 24"
                                        className="w-32 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg font-semibold text-center"
                                      />
                                      <span className="text-gray-600">hours before</span>
                                    </div>
                                  </div>

                                  <div className="space-y-3">
                                    <label className="block text-base font-semibold text-gray-900">
                                      Max reschedules per session
                                    </label>
                                    <div className="flex items-center gap-3">
                                      <input
                                        type="number"
                                        value={maxReschedulesPerSession}
                                        onChange={(e) => setMaxReschedulesPerSession(e.target.value)}
                                        min="0"
                                        max="10"
                                        className="w-32 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg font-semibold text-center"
                                      />
                                      <span className="text-gray-600">times per session</span>
                                    </div>
                                    <p className="text-sm text-gray-500">How many times can ONE session be rescheduled</p>
                                  </div>

                                  {/*
                                  COMMENTED OUT: Max total reschedules - not needed, per-session limit is sufficient
                                  <div className="space-y-3">
                                    <label className="block text-base font-semibold text-gray-900">
                                      Max total reschedules (across all sessions)
                                    </label>
                                    <div className="flex items-center gap-3">
                                      <input
                                        type="number"
                                        value={maxTotalReschedules}
                                        onChange={(e) => setMaxTotalReschedules(e.target.value)}
                                        min="0"
                                        max="100"
                                        className="w-32 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg font-semibold text-center"
                                      />
                                      <span className="text-gray-600">total reschedules</span>
                                    </div>
                                    <p className="text-sm text-gray-500">Total reschedules allowed across the entire series</p>
                                  </div>
                                  */}
                                </>
                              )}
                            </div>
                          </>
                        )}

                        <div className="flex gap-3 mt-6 pt-6 border-t">
                          <button
                            onClick={() => setShowCustomPolicyForm(false)}
                            className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={createCustomPolicy}
                            disabled={loadingPolicies}
                            className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loadingPolicies ? 'Saving Policy...' : activePolicy ? 'Update Policy' : 'Save Policy'}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>


                  {/* Inactive Policies List (if any) */}
                  {venuePolicies.filter((p) => p.booking_type === policyBookingType && !p.is_active).length > 0 && (
                    <div className="mt-6 border-t pt-6">
                      <h4 className="font-medium text-gray-900 mb-3">Inactive Policies</h4>
                      <div className="space-y-2">
                        {venuePolicies
                          .filter((p) => p.booking_type === policyBookingType && !p.is_active)
                          .map((policy) => (
                            <div
                              key={policy.id}
                              className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <h5 className="font-medium text-gray-700">{policy.name}</h5>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Full refund: {policy.full_refund_hours}h | Partial: {policy.partial_refund_percentage}% | Processing: {policy.refund_processing_days}
                                  </p>
                                </div>
                                {canEditPolicies && (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => activatePolicy(policy.id)}
                                      disabled={loadingPolicies}
                                      className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                                    >
                                      Activate
                                    </button>
                                    <button
                                      onClick={() => deletePolicy(policy.id)}
                                      disabled={loadingPolicies}
                                      className="px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded hover:bg-red-200 transition-colors disabled:opacity-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                </>
              )}
            </div>
          )}
        </div>

      </div>

      {/* ====================================================================
          FULLSCREEN IMAGE MODAL - EXACT ORIGINAL DESIGN
          ==================================================================== */}

      {isImageModalOpen && venue?.images && venue.images.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center"
          onClick={() => setIsImageModalOpen(false)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close button */}
          <button
            onClick={() => setIsImageModalOpen(false)}
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors z-10"
          >
            <X className="h-8 w-8" />
          </button>

          {/* Image counter */}
          {venue.images.filter((img: any) => typeof img === 'string' && img.trim() !== '').length > 1 && (
            <div className="absolute top-6 left-6 text-white text-lg font-medium z-10">
              {currentImageIndex + 1} / {venue.images.filter((img: any) => typeof img === 'string' && img.trim() !== '').length}
            </div>
          )}

          {/* Main image */}
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center px-20">
            {venue.images
              .filter((image: any) => typeof image === 'string' && image.trim() !== '')
              .map((image: string, index: number) => (
                <img
                  key={index}
                  src={image}
                  alt={`${venue.name} - Image ${index + 1}`}
                  className={`max-w-full max-h-full object-contain transition-opacity duration-300 ${
                    index === currentImageIndex ? 'opacity-100' : 'opacity-0 absolute'
                  }`}
                  onClick={(e) => e.stopPropagation()}
                />
              ))}
          </div>

          {/* Navigation arrows */}
          {venue.images.filter((img: any) => typeof img === 'string' && img.trim() !== '').length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const validImages = venue.images.filter((img: any) => typeof img === 'string' && img.trim() !== '');
                  setCurrentImageIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
                }}
                className="absolute left-6 top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-40 text-white p-4 rounded-full transition-all duration-200"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const validImages = venue.images.filter((img: any) => typeof img === 'string' && img.trim() !== '');
                  setCurrentImageIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
                }}
                className="absolute right-6 top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-40 text-white p-4 rounded-full transition-all duration-200"
              >
                <ChevronRight className="h-8 w-8" />
              </button>
            </>
          )}

          {/* Keyboard hint */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-sm opacity-60">
            Press ESC to close {venue.images.filter((img: any) => typeof img === 'string' && img.trim() !== '').length > 1 && '• Use arrow keys or click arrows to navigate'}
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
